<template>
  <div class="tags-wrapper">
    <div
      class="selected-tags-wrapper"
      :class="{ 'selected-widget-tags-wrapper': !isVideoLibrary }"
    >
      <div
        v-for="(tag, index) in getTagsNames(tags)"
        :key="tag"
        class="selected-tag-button"
      >
        <div class="selected-tag" :class="getTagColorBackground(index)">
          <p>
            {{ tag | truncate }}
          </p>
          <button
            class="close-icon"
            :class="getTagColor(index)"
            @click="deleteTagFromCampaign(index)"
          >
            <CloseIcon />
          </button>
        </div>
      </div>
    </div>
    <div
      v-if="showDisplayAddButton"
      class="add-tag-button-container"
      :class="{
        'library-add-tag-button-container': isVideoLibrary || isFeedManager,
      }"
    >
      <button
        class="add-tag-button"
        :class="{
          'library-tag-button': isVideoLibrary || isFeedManager,
          'feed-manager-tag-button': isFeedManager,
        }"
        @click="toggleAddButton"
      >
        {{ $t("campaignManager.tags.addTag") }}
      </button>
    </div>
    <div
      v-else-if="showTagsInput"
      v-click-outside="toggleAddButton"
      class="tags"
      :class="{ 'library-tags': isVideoLibrary }"
    >
      <input
        v-model="tagInput"
        class="search-tags"
        :class="{
          'library-search-tags': isVideoLibrary,
          'feed-manager-search-tags': isFeedManager,
        }"
        type="text"
        :placeholder="placeholder"
        autofocus
        maxlength="30"
        @keyup.enter="addTagToCampaign()"
      />
      <div class="tags-modal" :class="{ 'library-tags-modal': isVideoLibrary }">
        <p v-if="tagInput === ''" class="type-to-create-tag">
          {{ $t("campaignManager.tags.typeToCreate") }}
        </p>
        <p
          v-if="tagInput !== ''"
          class="create-tag-button"
          @click="addTagToCampaign()"
        >
          {{ $t("campaignManager.tags.create") }} "{{ tagInput }}"
        </p>

        <div
          v-for="(tag, index) in filteredTags"
          :key="index"
          class="tags-list"
        >
          <div
            v-if="!isTagAdded(tag)"
            class="tag"
            :class="getTagColor(index)"
            @click="addTagToCampaign(tag)"
          >
            <p>
              {{ tag.name }}
            </p>
            <button
              v-if="tag._id"
              class="trash-icon"
              @click="
                openDeleteTagModal(tag);
                $event.stopPropagation();
              "
            >
              <DeleteIcon
                v-if="isVideoLibrary"
                v-tippy="{ placement: 'top', arrow: true, boundary: 'window' }"
                :class="getTrashIconColor(index)"
                :content="$t('create.step2.addTagsToVideosModal.tooltip')"
              />
              <DeleteIcon v-else :class="getTrashIconColor(index)" />
            </button>
          </div>
        </div>
      </div>
    </div>
    <spinner-loader v-if="isLoading" />
  </div>
</template>
<script>
import CloseIcon from "@/assets/svg/settings/close.svg?inline";
import { mapGetters, mapState, mapMutations, mapActions } from "vuex";
import DeleteIcon from "@/assets/svg/delete-small.svg?inline";
import SpinnerLoader from "../shared/SpinnerLoader.vue";

export default {
  components: {
    SpinnerLoader,
    CloseIcon,
    DeleteIcon,
  },
  props: {
    campaign: {
      type: Object,
      default: null,
    },
    videos: {
      type: Array,
      default: null,
    },
    isVideoLibrary: {
      type: Boolean,
      default: false,
    },
    videoLibrary: {
      type: Array,
      default: () => [],
    },
    feed: {
      type: Object,
      default: null,
    },
    feedIndex: {
      type: Number,
      default: null,
    },
    isFeedManager: {
      type: Boolean,
      default: false,
    },
    campaignId: {
      type: String,
      default: "",
    },
    allFeeds: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      showAddButton: false,
      tagInput: "",
      tags: [],
      tagColors: [
        "blue",
        "red",
        "green",
        "orange",
        "purple",
        "brown",
        "light-blue",
        "yellow",
        "grey",
      ],
      placeholder: this.$t("campaignManager.tags.searchCreate"),
      isLoading: false,
    };
  },
  computed: {
    ...mapState({
      campaignsState: ({ campaign }) => campaign.campaigns,
    }),
    ...mapGetters({
      getSiteId: "account/getSiteId",
      tagsArray: "tag/getTags",
    }),
    ...mapMutations({
      updatedCampaign: "campaign/updateCampaign",
    }),

    showDisplayAddButton() {
      if (this.isVideoLibrary) return !this.showAddButton;
      if (this.isFeedManager)
        return this.tags.length < 2 && !this.showAddButton;
      return this.tags.length < 2 && !this.showAddButton;
    },
    filteredTags() {
      const base = Array.isArray(this.tagsArray)
        ? this.tagsArray.filter(Boolean)
        : [];
      if (this.videos && this.videos.length) {
        return base.filter((tag) => tag && tag.type === "library");
      }
      if (this.isFeedManager) {
        return base.filter((tag) => tag && tag.type === "feed");
      }

      return base.filter(
        (tag) =>
          tag && (!tag.type || (tag.type !== "library" && tag.type !== "feed"))
      );
    },
    showTagsInput() {
      if (this.isVideoLibrary) return !this.showDisplayAddButton;
      if (this.isFeedManager)
        return !this.showDisplayAddButton && this.tags.length < 2;
      return !this.showDisplayAddButton && this.tags.length < 2;
    },
  },
  watch: {
    "feed.tags": {
      immediate: true,
      handler(newTags) {
        if (!this.isFeedManager) return;
        this.tags = Array.isArray(newTags) ? [...new Set(newTags)] : [];
      },
    },

    isFeedManager(newVal) {
      if (newVal) {
        const newTags = this.feed?.tags;
        this.tags = Array.isArray(newTags) ? [...new Set(newTags)] : [];
      }
    },
  },

  async mounted() {
    if (this.isVideoLibrary) {
      const collected = this.videos
        .flatMap((video) => (Array.isArray(video.tags) ? video.tags : []))
        .filter((id, i, arr) => arr.indexOf(id) === i);
      this.tags = collected;
    } else if (this.isFeedManager) {
      this.tags = Array.isArray(this.feed?.tags)
        ? [...new Set(this.feed.tags)]
        : [];
    } else {
      this.tags = this.campaign.tags ? structuredClone(this.campaign.tags) : [];
    }
  },

  methods: {
    getTagColor(index) {
      const base =
        (this.filteredTags && this.filteredTags.length) ||
        this.tagColors.length;
      const colorIndex = index % base;
      const color = this.tagColors[colorIndex % this.tagColors.length];
      return color || "blue";
    },

    getTagColorBackground(index) {
      const tagId = this.tags[index];
      const colorIndex = this.filteredTags.findIndex(
        (tag) => tag._id === tagId
      );
      const color =
        this.tagColors[
          (colorIndex >= 0 ? colorIndex : 0) % this.tagColors.length
        ];
      return color ? color + "-bg" : "blue-bg";
    },

    getTrashIconColor(index) {
      const base =
        (this.filteredTags && this.filteredTags.length) ||
        this.tagColors.length;
      const colorIndex = index % base;
      const color = this.tagColors[colorIndex % this.tagColors.length];
      return color ? color + "-trash-icon" : "blue-trash-icon";
    },
    toggleAddButton() {
      this.showAddButton = !this.showAddButton;
    },

    async deleteTagFromCampaign(index) {
      const removedTag = this.tags[index];

      if (this.isFeedManager) {
        const current = Array.isArray(this.feed?.tags) ? this.feed.tags : [];
        const next = current.filter((id) => id !== removedTag);

        this.$store.commit(
          "campaign/setFeedTagsLocal",
          { feedIndex: this.feedIndex, tags: next },
          { root: true }
        );

        try {
          await this.$store.dispatch("campaign/updateFeedTagsOptimistic", {
            campaignId: this.campaignId,
            feedIndex: this.feedIndex,
            nextTags: next,
          });
          this.tags.splice(index, 1);

          this.$notify({
            title: this.$t("shared.toastMessage.success"),
            text: this.$t("campaignManager.tags.deleteTagFromCampaign"),
            type: "success",
          });
        } catch (err) {
          this.$notify({
            title: this.$t("shared.toastMessage.error"),
            text: err?.message || String(err),
            type: "error",
          });
        }
        return;
      }

      this.isLoading = true;
      try {
        if (this.isVideoLibrary) {
          const removed = this.tags.splice(index, 1)[0];
          for (const video of this.videos) {
            const updatedTags = (
              Array.isArray(video.tags) ? video.tags : []
            ).filter((id) => id !== removed);
            await this.$store.dispatch("video/updateVideoTags", {
              videoId: video._id,
              tags: updatedTags,
            });
          }
        } else {
          this.tags.splice(index, 1);
          await this.$store.dispatch("campaign/updateCampaign", {
            campaignId: this.campaign._id,
            dataToUpdate: { tags: this.tags },
          });
        }

        this.$notify({
          title: this.$t("shared.toastMessage.success"),
          text: this.$t("campaignManager.tags.deleteTagFromCampaign"),
          type: "success",
        });
      } finally {
        this.isLoading = false;
      }
    },
    async addTagToCampaign(tag = null) {
      if (this.isFeedManager) {
        const campaignId = this.campaignId;
        const feedIndex = this.feedIndex;

        if (tag?._id) {
          const id = tag._id;
          const current = Array.isArray(this.feed?.tags) ? this.feed.tags : [];
          const next = [...new Set([...current, id])];

          this.$store.commit(
            "campaign/setFeedTagsLocal",
            { feedIndex, tags: next },
            { root: true }
          );

          try {
            await this.$store.dispatch("campaign/updateFeedTagsOptimistic", {
              campaignId,
              feedIndex,
              nextTags: next,
            });
            this.$notify({
              title: this.$t("shared.toastMessage.success"),
              text: this.$t("campaignManager.tags.addTagToCampaign"),
              type: "success",
            });
          } catch (err) {
            this.$notify({
              title: this.$t("shared.toastMessage.error"),
              text: err?.message || String(err),
              type: "error",
            });
          } finally {
            this.showAddButton = false;
          }
          return;
        }

        const name = (tag ? tag.name : this.tagInput).trim();
        if (!name) return;

        const tempId = "__temp__" + Date.now();

        this.$store.commit("tag/addTag", { _id: tempId, name, type: "feed" });

        const current = Array.isArray(this.feed?.tags) ? this.feed.tags : [];
        const nextWithTemp = [...new Set([...current, tempId])];
        this.$store.commit(
          "campaign/setFeedTagsLocal",
          { feedIndex, tags: nextWithTemp },
          { root: true }
        );

        this.tags = nextWithTemp.slice();

        try {
          const created = await this.$store.dispatch("tag/createTag", {
            name,
            siteId: this.getSiteId,
            type: "feed",
          });

          this.$store.commit("tag/replaceTagId", { tempId, realTag: created });
          this.$store.commit(
            "campaign/replaceTagIdInFeedList",
            { tempId, realId: created._id },
            { root: true }
          );

          const nowTags =
            this.$store.state.campaign.feedList[feedIndex]?.tags || [];
          await this.$store.dispatch("campaign/updateFeedTagsOptimistic", {
            campaignId,
            feedIndex,
            nextTags: nowTags,
          });

          this.$notify({
            title: this.$t("shared.toastMessage.success"),
            text: this.$t("campaignManager.tags.addTagToCampaign"),
            type: "success",
          });
        } catch (err) {
          const rollback = (
            this.$store.state.campaign.feedList[feedIndex]?.tags || []
          ).filter((id) => id !== tempId);
          this.$store.commit(
            "campaign/setFeedTagsLocal",
            { feedIndex, tags: rollback },
            { root: true }
          );
          this.$store.commit("tag/deleteTag", tempId);

          this.$notify({
            title: this.$t("shared.toastMessage.error"),
            text: err?.message || String(err),
            type: "error",
          });
        } finally {
          this.tagInput = "";
          this.showAddButton = false;
        }

        return;
      }

      this.isLoading = true;
      try {
        let newId;
        if (tag?._id) {
          newId = tag._id;
        } else {
          const created = await this.createTag(
            tag ? tag.name : this.tagInput.trim()
          );
          if (!created) throw new Error("Tag creation failed");
          newId = created._id;
          this.tagInput = "";
        }

        this.tags.push(newId);

        if (this.isVideoLibrary) {
          for (const video of this.videos) {
            const updatedTags = [
              ...new Set([
                ...(Array.isArray(video.tags) ? video.tags : []),
                newId,
              ]),
            ];
            await this.$store.dispatch("video/updateVideoTags", {
              videoId: video._id,
              tags: updatedTags,
            });
          }
        } else {
          await this.$store.dispatch("campaign/updateCampaign", {
            campaignId: this.campaign._id,
            dataToUpdate: { tags: this.tags },
          });
        }

        this.$notify({
          title: this.$t("shared.toastMessage.success"),
          text: this.$t("campaignManager.tags.addTagToCampaign"),
          type: "success",
        });
      } finally {
        this.isLoading = false;
        this.showAddButton = false;
      }
    },
    async createTag(tagName) {
      try {
        this.isLoading = true;
        const siteId = this.getSiteId;
        const tagData = { name: tagName, siteId };
        if (this.videos && this.videos.length) {
          tagData.type = "library";
        } else if (this.isFeedManager) tagData.type = "feed";
        const tag = await this.$store.dispatch("tag/createTag", tagData);
        this.isLoading = false;
        return tag;
      } catch (err) {
        this.isLoading = false;
        console.log(err);
      }
    },
    getTagsNames(tags) {
      if (tags.length === 0) {
        return [];
      }
      const names = [];
      tags.forEach((id) => {
        const tagName = this.findTagNameById(id);
        if (tagName) {
          names.push(tagName);
        } else {
          const tagToDelete = this.tags.findIndex((tag) => tag._id === id);
          this.tags.splice(tagToDelete, 1);
          this.$store.dispatch("campaign/updateCampaign", {
            campaignId: this.campaign._id,
            dataToUpdate: { tags: this.tags },
          });
        }
      });
      return names;
    },
    findTagNameById(id) {
      const tag = this.tagsArray.find((tag) => tag._id === id);
      return tag ? tag.name : null;
    },
    async openDeleteTagModal(tag) {
      if (this.isVideoLibrary) {
        await this.$store.dispatch("tag/deleteTag", tag._id);
        for (const video of this.videoLibrary) {
          if (!Array.isArray(video.tags) || video.tags.length === 0) {
            continue;
          }
          const newTags = (video.tags || []).filter((id) => id !== tag._id);
          await this.$store.dispatch("video/updateVideoTags", {
            videoId: video._id,
            tags: newTags,
          });
        }

        this.tags = this.tags.filter((id) => id !== tag._id);

        this.$notify({
          title: this.$t("shared.toastMessage.success"),
          text: this.$t("campaignManager.tags.deleteTagFromCampaign"),
          type: "success",
        });
        return;
      }

      if (this.isFeedManager) {
        try {
          this.isLoading = true;
          await this.$store.dispatch("tag/deleteTag", tag._id);

          const updates = [];
          this.allFeeds.forEach((feed, feedIndex) => {
            const current = Array.isArray(feed.tags) ? feed.tags : [];
            if (current.includes(tag._id)) {
              const updatedFeed = {
                ...feed,
                tags: current.filter((id) => id !== tag._id),
                modificationDate: new Date().toISOString(),
              };
              updates.push(
                this.$store.dispatch("campaign/updateFeed", {
                  campaignId: this.campaignId,
                  feedIndex,
                  feedData: updatedFeed,
                })
              );
            }
          });
          await Promise.all(updates);

          this.tags = this.tags.filter((id) => id !== tag._id);

          this.$notify({
            title: this.$t("shared.toastMessage.success"),
            text: this.$t("campaignManager.tags.deleteTagFromCampaign"),
            type: "success",
          });
        } catch (err) {
          this.$notify({
            title: this.$t("shared.toastMessage.error"),
            text: err?.message || String(err),
            type: "error",
          });
        } finally {
          this.isLoading = false;
        }
        return;
      }
      this.$emit("open-delete-tag-modal", tag);
    },
    isTagAdded(tag) {
      return this.tags.includes(tag._id);
    },
  },
};
</script>

<style lang="scss" scoped>
.tags-modal {
  padding: 8px;
  width: 185px;
  min-height: 30px;
  z-index: 99;
  background: white;
  position: absolute;
  top: 30px;
  right: -20px;
  border: 1px solid $light-grey;
  border-radius: 4px;
  box-sizing: border-box;
  max-height: 170px;
  overflow-y: auto;
}

.tags-wrapper {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
}

.selected-tags-wrapper {
  display: flex;
  max-width: 500px;
  flex-wrap: wrap;
  gap: 8px;
  align-items: flex-start;
}

.selected-widget-tags-wrapper {
  flex-direction: column;
}

.add-tag-button {
  @include font-small;
  @include base-font;
  color: $medium-grey;
  padding: 3px 8px;
  background-color: white;
  border: 1px solid $light-grey;
  border-radius: 3px;
  min-width: 120px;
  height: 28px;
  box-sizing: border-box;
  &:hover {
    color: $dark-purple;
    border: 1px solid $dark-purple;
  }
}

.search-tags {
  @include font-small;
  @include base-font;
  padding: 3px 14px 3px 8px;
  width: 145px;
  height: 23px;
  box-sizing: border-box;
  border: 1px solid $dark-purple;
  border-radius: 3px;
  color: $dark-grey;
  &:focus {
    outline: 1px solid $dark-purple;
  }
}

.tags-list {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.tags {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.tag {
  @include font-small;
  @include base-font;
  padding: 4px 8px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  &:hover {
    cursor: pointer;
  }
  &:hover .tag-name,
  &:hover .delete-icon {
    background-color: $light-grey;
  }
  &:hover .trash-icon {
    path {
      fill-opacity: 1;
    }
  }
}

.tag:hover .tag-name,
.tag:hover .delete-icon {
  background-color: inherit;
}
.create-tag-button {
  @include font-small;
  @include base-font;
  font-weight: 600;
  padding: 4px 8px;
  overflow-wrap: break-word;
  background-color: rgba(88, 88, 88, 0.2);
  &:hover {
    cursor: pointer;
  }
}
.type-to-create-tag {
  padding: 4px;
  font-style: italic;
  @include font-small;
  color: $light-grey;
  margin-bottom: 2px;
}

.selected-tag {
  @include font-small;
  @include base-font;
  display: flex;
  padding: 4px 8px;
  border-radius: 4px;
  overflow: ellipsis;
  box-sizing: border-box;
  white-space: nowrap;
  &:hover {
    cursor: default;
  }
}

.close-icon {
  transform: scale(50%);
  background: transparent;
}

.trash-icon {
  transform: scale(150%);
  margin: 0 6px;
  background: transparent;
  path {
    fill-opacity: 0;
  }
}

.blue {
  color: rgba(0, 48, 211, 1);
  &:hover {
    background-color: rgba(0, 48, 211, 0.2);
  }
}
.red {
  color: rgba(181, 3, 14, 1);
  &:hover {
    background-color: rgba(181, 3, 14, 0.2);
  }
}
.green {
  color: rgba(2, 162, 0, 1);
  &:hover {
    background-color: rgba(2, 162, 0, 0.2);
  }
}
.orange {
  color: rgba(236, 128, 0, 1);
  &:hover {
    background-color: rgba(236, 128, 0, 0.2);
  }
}
.purple {
  color: rgba(211, 0, 203, 1);
  &:hover {
    background-color: rgba(211, 0, 203, 0.2);
  }
}
.brown {
  color: rgba(154, 91, 35, 1);
  &:hover {
    background-color: rgba(154, 91, 35, 0.2);
  }
}
.light-blue {
  color: rgba(0, 188, 188, 1);
  &:hover {
    background-color: rgba(0, 188, 188, 0.2);
  }
}
.yellow {
  color: rgba(207, 156, 0, 1);
  &:hover {
    background-color: rgba(207, 156, 0, 0.2);
  }
}
.grey {
  color: rgba(88, 88, 88, 1);
  &:hover {
    background-color: rgba(88, 88, 88, 0.2);
  }
}

.blue-bg {
  color: rgba(0, 48, 211, 1);
  background-color: rgba(0, 48, 211, 0.2);
  svg {
    path {
      fill: rgba(0, 48, 211, 1);
      opacity: 1;
    }
  }
}
.red-bg {
  color: rgba(181, 3, 14, 1);
  background-color: rgba(181, 3, 14, 0.2);
  svg {
    path {
      fill: rgba(181, 3, 14, 1);
    }
  }
}
.green-bg {
  color: rgba(2, 162, 0, 1);
  background-color: rgba(2, 162, 0, 0.2);
  svg {
    path {
      fill: rgba(2, 162, 0, 1);
    }
  }
}
.orange-bg {
  color: rgba(236, 128, 0, 1);
  background-color: rgba(236, 128, 0, 0.2);
  svg {
    path {
      fill: rgba(236, 128, 0, 1);
    }
  }
}
.purple-bg {
  color: rgba(211, 0, 203, 1);
  background-color: rgba(211, 0, 203, 0.2);
  svg {
    path {
      fill: rgba(211, 0, 203, 1);
    }
  }
}
.brown-bg {
  color: rgba(154, 91, 35, 1);
  background-color: rgba(154, 91, 35, 0.2);
  svg {
    path {
      fill: rgba(154, 91, 35, 1);
    }
  }
}
.light-blue-bg {
  color: rgba(0, 188, 188, 1);
  background-color: rgba(0, 188, 188, 0.2);
  svg {
    path {
      fill: rgba(0, 188, 188, 1);
    }
  }
}
.yellow-bg {
  color: rgba(207, 156, 0, 1);
  background-color: rgba(207, 156, 0, 0.2);
  svg {
    path {
      fill: rgba(207, 156, 0, 1);
    }
  }
}
.grey-bg {
  color: rgba(88, 88, 88, 1);
  background-color: rgba(88, 88, 88, 0.2);
  svg {
    path {
      fill: rgba(88, 88, 88, 1);
      opacity: 1;
    }
  }
}

.blue-trash-icon {
  path {
    fill: rgba(0, 48, 211, 1);
  }
}
.red-trash-icon {
  path {
    fill: rgba(181, 3, 14, 1);
  }
}
.green-trash-icon {
  path {
    fill: rgba(2, 162, 0, 1);
  }
}
.orange-trash-icon {
  path {
    fill: rgba(236, 128, 0, 1);
  }
}
.purple-trash-icon {
  path {
    fill: rgba(211, 0, 203, 1);
  }
}
.brown-trash-icon {
  path {
    fill: rgba(154, 91, 35, 1);
  }
}
.light-blue-trash-icon {
  path {
    fill: rgba(0, 188, 188, 1);
  }
}
.yellow-trash-icon {
  path {
    fill: rgba(207, 156, 0, 1);
  }
}
.grey-trash-icon {
  path {
    fill: rgba(88, 88, 88, 1);
  }
}
.library-add-tag-button-container {
  width: 100%;
}
.library-tag-button {
  width: 100%;
  height: 33px;
}

// .feed-manager-tag-button {
//   min-width: 100px;
//   width: 100px;
// }

.library-tags {
  width: 100%;
}

.library-search-tags {
  width: 100%;
  height: 33px;
}

.library-tags-modal {
  width: 100%;
  right: 0;
  top: 35px;
}
</style>
